import { replace } from "radash";
import { useCallback, useEffect, useState } from "react";
import { getUploadEventName } from "./helpers/events";
import type { Upload } from "./schema";
import { cancelUpload as onUploadCancel } from "./upload-file";

const CANCEL_EVENT = "upload-cancelled";
const REMOVE_EVENT = "upload-removed";

function useCustomEvent<T>(callback: (detail: T) => void, eventName: string) {
  useEffect(() => {
    function eventHandler(event: Event) {
      if ("detail" in event) {
        callback(event.detail as T);
      }
    }
    document.addEventListener(eventName, eventHandler);

    return () => {
      document.removeEventListener(eventName, eventHandler);
    };
  }, []);
}

export function useUploads(key?: string) {
  const [uploads, setUploads] = useState<Array<Upload>>([]);

  const removeUpload = useCallback((uploadId: string) => {
    const event = new CustomEvent(REMOVE_EVENT, { detail: { uploadId } });
    document.dispatchEvent(event);
  }, []);

  const cancelUpload = useCallback((uploadId: string | undefined) => {
    const event = new CustomEvent(CANCEL_EVENT, { detail: { uploadId } });
    document.dispatchEvent(event);
  }, []);

  useCustomEvent<Upload>((upload) => {
    setUploads((uploads) => {
      if (uploads.some((u) => u.uploadId === upload.uploadId)) {
        return replace(uploads, upload, (u) => u.uploadId === upload.uploadId);
      }
      return [...uploads, upload];
    });
  }, getUploadEventName());

  useCustomEvent<{ uploadId: string }>(({ uploadId }) => {
    setUploads((uploads) => uploads.filter((u) => u.uploadId !== uploadId));
  }, REMOVE_EVENT);

  useCustomEvent<{ uploadId: string }>(({ uploadId }) => {
    onUploadCancel(uploadId || "");
    removeUpload(uploadId || "");
  }, CANCEL_EVENT);

  return {
    uploads: uploads.filter((upload) => !key || upload.key === key),
    removeUpload,
    cancelUpload,
  };
}
